import React, { useMemo } from "react";
import { ComposedChart, Line, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { fillTimeline, fmtBytes, fmtPct, fmtTick, fmtUptime, fmtWib } from "./monUtil";

const EMPTY = { cpu_percent: null, cpu_max: null, mem_percent: null, disk_percent: null, process_cpu_percent: null, uptime_sec: null };

function Tip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="rounded-xl border border-line bg-white p-3 text-xs shadow-lg">
      <p className="font-bold text-slate-900">{fmtWib(new Date(p.ts).toISOString(), { dateStyle: "short", timeStyle: "short" })}</p>
      {p.cpu_percent == null ? <p className="mt-1 text-slate-400">Tidak ada snapshot</p> : (
        <>
          <p className="mt-1 text-slate-600">CPU <b>{fmtPct(p.cpu_percent)}</b> (puncak {fmtPct(p.cpu_max)}) · proses {fmtPct(p.process_cpu_percent)}</p>
          <p className="text-slate-600">RAM <b>{fmtPct(p.mem_percent)}</b> · {fmtBytes(p.mem_used)} · RSS {fmtBytes(p.process_rss)}</p>
          <p className="text-slate-600">Disk {fmtPct(p.disk_percent)} · uptime {fmtUptime(p.uptime_sec)}</p>
        </>
      )}
    </div>
  );
}

export function SystemChart({ series, win, step }) {
  const pts = useMemo(() => (series ? fillTimeline(series.points || [], win, step, EMPTY) : []), [series, win, step]);
  const restarts = useMemo(() => pts.filter((p, i) => { const prev = pts.slice(0, i).reverse().find((x) => x.uptime_sec != null); return p.uptime_sec != null && prev && p.uptime_sec < prev.uptime_sec; }).length, [pts]);
  if (!series) return <div className="h-72 animate-pulse rounded-2xl bg-slate-50" data-testid="mon-system-chart-empty" />;
  const tick = (v) => fmtTick(v, win);
  return (
    <div data-testid="mon-system-chart">
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={pts} margin={{ left: 0, right: 8, top: 4 }}>
            <CartesianGrid stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="ts" type="number" domain={["dataMin", "dataMax"]} scale="time" tickFormatter={tick} tick={{ fontSize: 10, fill: "#94A3B8" }} minTickGap={40} />
            <YAxis domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10, fill: "#94A3B8" }} width={44} />
            <Tooltip content={<Tip />} /><Legend wrapperStyle={{ fontSize: 11 }} />
            <Area dataKey="cpu_max" name="CPU puncak" stroke="none" fill="#7C3AED" fillOpacity={0.08} isAnimationActive={false} connectNulls={false} />
            <Line dataKey="cpu_percent" name="CPU" stroke="#2563EB" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} />
            <Line dataKey="mem_percent" name="RAM" stroke="#06B6D4" strokeWidth={2} dot={false} isAnimationActive={false} connectNulls={false} />
            <Line dataKey="disk_percent" name="Disk" stroke="#94A3B8" strokeWidth={1.5} strokeDasharray="4 3" dot={false} isAnimationActive={false} connectNulls={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      {restarts > 0 && <p className="mt-2 text-[11px] font-semibold text-amber-700" data-testid="mon-restarts">Terdeteksi {restarts}× restart/redeploy (uptime turun) dalam window ini.</p>}
    </div>
  );
}
